import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { DropZone } from "@/components/elements/DropZone";
import { TFileRules } from "@/utils/file";
import { TExtendFile } from "@/types/file";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { useGetNationalityList, useGetResidenceStatusList } from "@/api/services/global/master";
import { JOB_DOCUMENT_FILE_MINE_TYPES, JOB_DOCUMENT_FILE_TYPES_EXTENSION, MAX_JOB_DOCUMENT_FILE, MAX_SIZE } from "@/features/job/constants/file";

type JobRequirementProps = {
  isDraft?: boolean;
};

export const JobRequirement: FC<JobRequirementProps> = ({ isDraft }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
  const { data: genderOptions } = useEnum("EGender");
  const { data: specifiedSkillPassedOptions } = useEnum("ESpecifiedSkillPassed");
  const { nationalityOptions, isLoading: isLoadingNationality } = useGetNationalityList();
  const { residenceStatusOptions, isLoading: isLoadingResidenceStatus } = useGetResidenceStatusList();
  const rules: TFileRules = { acceptMimeTypes: JOB_DOCUMENT_FILE_MINE_TYPES, acceptFileTypesExtension: JOB_DOCUMENT_FILE_TYPES_EXTENSION };

  const jobDocumentFile = useWatch({ control, name: "jobDocumentFile" }) || [];
  const jobDocumentFilePath = useWatch({ control, name: "jobDocumentFilePath" }) || null;

  useEffect(() => {
    if (jobDocumentFilePath && jobDocumentFile.length === 0) {
      const file: TExtendFile = {
        name: jobDocumentFilePath.split("/").pop() || "",
        filePath: jobDocumentFilePath,
        type: jobDocumentFilePath.split(".").pop() || "",
      } as unknown as TExtendFile;
      setValue("jobDocumentFile", [file]);
    }
  }, [jobDocumentFilePath, jobDocumentFile, setValue]);

  const handleChangeFile = async (value: TExtendFile[] | TExtendFile | null) => {
    if (value === null) {
      setValue("jobDocumentFile", null);
      return;
    }
    const validFile = value as TExtendFile;
    setValue("jobDocumentFile", [validFile]);

  };

  const handleFileRemove = () => {
    setValue("jobDocumentFile", null);

    setValue("jobDocumentFilePath", null);
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="応募要件" /> } showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="japaneseLevelType">
              <Select placeholder="選択してください" options={japaneseLevelTypeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="japaneseLevelOther">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="skillsExperience">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="otherApplicationRequirements">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="ageLimit">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="nationalityIds" required={!isDraft}>
              <SelectMulti
                placeholder="選択してください"
                loading={isLoadingNationality}
                options={nationalityOptions}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="recruitmentLocationType" required={!isDraft}>
              <Select placeholder="選択してください" options={recruitmentLocationTypeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="desiredHiringPeriod">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="gender">
              <RadioGroup direction="row" options={genderOptions}/>
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="residenceStatusIds" required={!isDraft}>
              <SelectMulti
                placeholder="選択してください"
                loading={isLoadingResidenceStatus}
                options={residenceStatusOptions}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="specifiedSkillPassed">
              <SelectMulti
                placeholder="選択してください"
                options={specifiedSkillPassedOptions}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="jobDocumentUrl">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="cap12">URL</Typography>
                <Input placeholder="入力してください" fullWidth/>
              </Stack>
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <DropZone
              options={{ multiple: false }}
              bordered={true}
              rules={{
                maxFile: MAX_JOB_DOCUMENT_FILE,
                maxSize: MAX_SIZE,
                acceptFileTypesExtension: rules.acceptFileTypesExtension,
                acceptMimeTypes: rules.acceptMimeTypes,
              }}
              enableFileList={false}
              enableFileListCustom={true}
              onChange={handleChangeFile}
              onFileRemove={handleFileRemove}
              value={methods.getValues("jobDocumentFile")}
              noClick={true}
              noKeyboard={true}
              nonControl
            ></DropZone>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="selectionProcess">
              <Input placeholder="入力してください"/>
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};
